import { AudioSample } from 'mediabunny';

/**
 * Mediabunny's live audio sources hand each captured block to the encoder without waiting for the previous
 * one (`void encoder.add(sample)`). A PCM block longer than 2048 frames is written as several packets with
 * an await between them, so when blocks arrive in a burst (Safari delivers 4096-frame blocks, and a busy
 * or backgrounded tab releases a second of them at once) packets from later blocks overtake earlier ones.
 * The WAV muxer then rejects the take ("Timestamps cannot be smaller than the largest timestamp of the
 * previous GOP"). Queue the blocks so each is fully written before the next starts: audio is small, so
 * a backlog costs little memory, and nothing is dropped.
 */
export function serializeAudioAdds(source: object, onArrival?: (sample: AudioSample, original: AudioSample) => void) {
  const encoder = (source as any)._encoder;
  if (!encoder || typeof encoder.add !== 'function') {
    console.warn('[takehouse] audio source internals changed; blocks are not queued in order');
    return;
  }
  const add = encoder.add.bind(encoder);
  let tail: Promise<unknown> = Promise.resolve();
  let channels: number | null = null;
  encoder.add = (sample: AudioSample, shouldClose: boolean) => {
    const original = sample;
    // The encoder rejects any change in channel count, which ends the recording. Microphones do change
    // (stereo at start-up then mono, or a headset reconnecting), so keep the count the file started with.
    channels ??= sample.numberOfChannels;
    if (sample.numberOfChannels !== channels) {
      const fixed = remixChannels(sample, channels);
      if (shouldClose) sample.close();
      sample = fixed;
      shouldClose = true;
    }
    // Runs as the block arrives, before it waits in the queue: wall-clock readings taken here are honest.
    onArrival?.(sample, original);
    const run = tail.then(() => add(sample, shouldClose));
    tail = run.catch(() => {});
    return run;
  };
}

let remixWarned = false;
/** Same audio, different channel count: mono is copied to every channel, more channels are averaged down. */
export function remixChannels(sample: AudioSample, target: number): AudioSample {
  const frames = sample.numberOfFrames;
  const source = sample.numberOfChannels;
  const planes = Array.from({ length: source }, (_, i) => {
    const p = new Float32Array(frames);
    sample.copyTo(p, { planeIndex: i, format: 'f32-planar' });
    return p;
  });
  const out = new Float32Array(frames * target);
  for (let c = 0; c < target; c++) {
    const dst = out.subarray(c * frames, (c + 1) * frames);
    if (target === 1) {
      for (const p of planes) for (let k = 0; k < frames; k++) dst[k] += p[k] / source;
    } else dst.set(planes[Math.min(c, source - 1)]);
  }
  if (!remixWarned) {
    remixWarned = true;
    console.warn(`[takehouse] microphone changed from ${target} to ${source} channel(s); converting to keep the recording going`);
  }
  return new AudioSample({ data: out, format: 'f32-planar', numberOfChannels: target, sampleRate: sample.sampleRate, timestamp: sample.timestamp });
}
